/**
 * Pure rules of Bobo's cameo (the occasional few-second appearance): which screen maps to which tip topic, which
 * message comes next (no repeats until the pool is used up) and when a cameo may show at all (rate limiter).
 * No React Native imports, so everything here is unit-testable. Mirror of the admin implementation.
 *
 * @module components/bobo/cameo/cameoLogic
 */

/** First cameo only after this much foreground time in the session. */
export const FIRST_CAMEO_AFTER_MS = 60 * 1000;
/** Minimum gap between two cameos. */
export const MIN_GAP_MS = 15 * 60 * 1000;
/** Maximum cameos per calendar day. */
export const MAX_PER_DAY = 3;
/** How long a cameo stays, a tap adds {@link EXTEND_MS}. */
export const VISIBLE_MS = 8000;
export const EXTEND_MS = 4000;

/** Real screen names (app/components/AppNavigator.jsx) -> tip topic. */
const TOPIC_BY_ROUTE = {
    AIAssistantScreen: 'ai',
    NotificationsScreen: 'notifications',
    HappyAiInsightsScreen: 'insights',
    BusinessDigestScreen: 'digest',
    BusinessAlertsScreen: 'alerts',
    BusinessGoalsScreen: 'goals',
    FinanceIntelligenceScreen: 'finance',
    ManagementFinanceScreen: 'finance',
    CostIntelligenceScreen: 'cost',
    StockIntelligenceScreen: 'stock',
    WarehousemanHomeScreen: 'stock',
    HomeScreen: 'home',
    PosHubScreen: 'home',
    ManagementHomeScreen: 'home',
    SalesHomeScreen: 'home',
    StatisticsScreen: 'statistics',
    MailHomeScreen: 'mail',
    ChatHomeScreen: 'chat',
    SettingsScreen: 'settings',
};

/** Topics that have two tips each (keys `boboCameo_tip_<topic>_<1|2>`). */
export const TOPICS = Object.freeze([...new Set(Object.values(TOPIC_BY_ROUTE))]);
export const TIPS_PER_TOPIC = 2;
export const GENERIC_COUNT = 10;

/** Fullscreen task flows that must never be interrupted. */
const BLOCKED_ROUTES = new Set([
    'IncomingCall', 'ChatMeetingScreen', 'MeetingRecorderScreen', 'ResultScreen', 'WaitingForPayment',
    'SuccessEntryScreen', 'FailedEntryScreen',
]);
const BLOCKED_PATTERN = /login|onboard|payment|kiosk|camera|scan|register|cash|call|dialer|compose|editor|builder|closing|opening|ticketentry|2fa|twofactor|password/i;

/**
 * Tip topic of a screen.
 *
 * @param {(string|null|undefined)} routeName Navigation route name.
 * @returns {string} A topic from {@link TOPICS}, or 'generic'.
 */
export function resolveCameoTopic(routeName) {
    return (routeName && TOPIC_BY_ROUTE[routeName]) || 'generic';
}

/**
 * Whether the screen is a fullscreen task flow (login, payment, scanner, ...) where Bobo must stay away.
 *
 * @param {(string|null|undefined)} routeName Navigation route name.
 * @returns {boolean} True when blocked. Unknown route (nothing focused yet) counts as blocked.
 */
export function isBlockedRoute(routeName) {
    if (!routeName) {
        return true;
    }

    return BLOCKED_ROUTES.has(routeName) || BLOCKED_PATTERN.test(routeName);
}

/**
 * Time-of-day greeting slot.
 *
 * @param {number} hour 0..23.
 * @returns {string} 'morning' | 'day' | 'evening'.
 */
export function greetingSlot(hour) {
    if (hour >= 5 && hour < 11) {
        return 'morning';
    }

    return hour >= 11 && hour < 17 ? 'day' : 'evening';
}

const make = (id, kind, textKey, mood) => ({id, kind, titleKey: `boboCameo_title_${kind}`, textKey, mood});

/**
 * Candidate messages in priority order: topic tips, then the time-of-day greeting, then witty lines.
 *
 * @param {string} topic Topic.
 * @param {number} hour Local hour.
 * @returns {Array<{id: string, kind: string, titleKey: string, textKey: string, mood: string}>} Candidates.
 */
function candidates(topic, hour) {
    const list = [];

    if (topic !== 'generic' && TOPICS.includes(topic)) {
        for (let i = 1; i <= TIPS_PER_TOPIC; i += 1) {
            list.push(make(`tip_${topic}_${i}`, 'tip', `boboCameo_tip_${topic}_${i}`, 'thinking'));
        }
    }
    const slot = greetingSlot(hour);

    list.push(make(`greet_${slot}`, 'greet', `boboCameo_greet_${slot}`, 'greet'));
    for (let i = 1; i <= GENERIC_COUNT; i += 1) {
        list.push(make(`wit_${i}`, 'wit', `boboCameo_wit_${i}`, i % 3 === 0 ? 'love' : 'happy'));
    }

    return list;
}

/**
 * Pick the next cameo message: topic tips first, then greeting, then witty lines; never one already seen until
 * every candidate for this context has been seen (then the pool starts over and `reset` is true).
 *
 * @param {Object} args Arguments.
 * @param {string} [args.topic] Topic from {@link resolveCameoTopic}.
 * @param {number} [args.hour] Local hour 0..23.
 * @param {string[]} [args.seenIds] Ids shown before.
 * @param {() => number} [args.random] RNG in [0, 1) used inside the generic pool.
 * @returns {{id: string, kind: string, titleKey: string, textKey: string, mood: string, reset: boolean}} Message.
 */
export function pickCameoMessage({topic = 'generic', hour = 12, seenIds = [], random = Math.random} = {}) {
    const all = candidates(topic, hour);
    const seen = new Set(seenIds);
    let pool = all.filter((c) => !seen.has(c.id));
    const reset = pool.length === 0;

    if (reset) {
        pool = all;
    }
    // Tips and the greeting keep their order (most useful first); only the witty lines are randomised.
    const ordered = pool.filter((c) => c.kind !== 'wit');
    const choice = ordered.length ? ordered[0] : null;

    if (choice) {
        return {...choice, reset};
    }
    const index = Math.min(pool.length - 1, Math.floor(random() * pool.length));

    return {...pool[index], reset};
}

/**
 * Local calendar day key.
 *
 * @param {number} now Epoch ms.
 * @returns {string} yyyy-mm-dd.
 */
export function dayKey(now) {
    const d = new Date(now);

    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/**
 * Rate limiter and context gate.
 *
 * @param {Object} state Persisted + session state.
 * @param {boolean} [state.disabled] "Don't show again".
 * @param {boolean} [state.snoozed] Closed with X this session.
 * @param {number} [state.foregroundMs] Foreground time this session.
 * @param {(number|null)} [state.lastShownAt] Epoch ms of the last cameo.
 * @param {number} [state.shownToday] Cameos shown on `state.shownDay`.
 * @param {(string|null)} [state.shownDay] Day key of `shownToday`.
 * @param {number} now Epoch ms.
 * @param {Object} ctx Current context.
 * @param {boolean} [ctx.appActive] App in foreground.
 * @param {boolean} [ctx.keyboardVisible] Keyboard open.
 * @param {boolean} [ctx.modalOpen] A modal/overlay is open (when detectable).
 * @param {(string|null)} [ctx.routeName] Focused route.
 * @param {boolean} [ctx.reducedMotion] Reduced motion is on (cameo is then a calm static bubble).
 * @returns {{show: boolean, calm: boolean, reason: (string|null)}} Decision.
 */
export function shouldShowCameo(state = {}, now = Date.now(), ctx = {}) {
    const calm = !!ctx.reducedMotion;
    const no = (reason) => ({show: false, calm, reason});

    if (state.disabled) {
        return no('disabled');
    }
    if (state.snoozed) {
        return no('snoozed');
    }
    if (!ctx.appActive) {
        return no('inactive');
    }
    if (ctx.keyboardVisible) {
        return no('keyboard');
    }
    if (ctx.modalOpen) {
        return no('modal');
    }
    if (isBlockedRoute(ctx.routeName)) {
        return no('route');
    }
    if ((state.foregroundMs || 0) < FIRST_CAMEO_AFTER_MS) {
        return no('warmup');
    }
    if (state.lastShownAt && now - state.lastShownAt < MIN_GAP_MS) {
        return no('gap');
    }
    if (state.shownDay === dayKey(now) && (state.shownToday || 0) >= MAX_PER_DAY) {
        return no('daily');
    }

    return {show: true, calm, reason: null};
}
